import { ReactElement } from 'react';

import { generateBorderId, generateFillId, generateSVGIcon, generateSvgId } from './Shapes.helpers';
import { ResizeShape, ShapePathProps, ShapeProps } from './Shapes.types';

const generateOctagonFillPath = ({ widthPx, heightPx, borderWidth }: ShapePathProps) => {
  const theta = Math.atan(heightPx / widthPx);
  const offsetY = heightPx / (2 + Math.sqrt(2));
  const offsetX = offsetY / Math.tan(theta);
  const deltaY = borderWidth / Math.tan((Math.PI + 2 * theta) / 4);
  const deltaX = borderWidth / Math.tan((Math.PI - theta) / 2);
  return `
    M ${offsetX + deltaX}, ${borderWidth}
    L ${widthPx - offsetX - deltaX}, ${borderWidth}
    L ${widthPx - borderWidth}, ${offsetY + deltaY}
    L ${widthPx - borderWidth}, ${heightPx - offsetY - deltaY}
    L ${widthPx - offsetX - deltaX}, ${heightPx - borderWidth}
    L ${offsetX + deltaX}, ${heightPx - borderWidth}
    L ${borderWidth} ${heightPx - offsetY - deltaY}
    L ${borderWidth} ${offsetY + deltaY}
    Z
  `;
};

const generateOctagonBorderPath = ({ widthPx, heightPx, borderWidth }: ShapePathProps) => {
  const theta = Math.atan(heightPx / widthPx);
  const offsetY = heightPx / (2 + Math.sqrt(2));
  const offsetX = offsetY / Math.tan(theta);
  const deltaY = borderWidth / 2 / Math.tan((Math.PI + 2 * theta) / 4);
  const deltaX = borderWidth / 2 / Math.tan((Math.PI - theta) / 2);
  return `
    M ${offsetX + deltaX}, ${borderWidth / 2}
    L ${widthPx - offsetX - deltaX}, ${borderWidth / 2}
    L ${widthPx - borderWidth / 2}, ${offsetY + deltaY}
    L ${widthPx - borderWidth / 2}, ${heightPx - offsetY - deltaY}
    L ${widthPx - offsetX - deltaX}, ${heightPx - borderWidth / 2}
    L ${offsetX + deltaX}, ${heightPx - borderWidth / 2}
    L ${borderWidth / 2} ${heightPx - offsetY - deltaY}
    L ${borderWidth / 2} ${offsetY + deltaY}
    Z
  `;
};

const OCTAGON_CONSTANT = 0.707;
export const resizeOctagon = ({ widthPx, heightPx, borderWidth, id, options }: ResizeShape) => {
  const svg = document.getElementById(generateSvgId(id));
  const svgFill = document.getElementById(generateFillId(id));
  const svgBorder = document.getElementById(generateBorderId(id));
  const { isResponsiveGroupResize, contentHeightPx } = options || {};
  if (svg && svgFill && svgBorder) {
    const originalWidth = parseFloat(svg.getAttribute('width') || '0');
    const originalHeight = parseFloat(svg.getAttribute('height') || '0');
    const is1to1 = originalWidth === originalHeight;
    let newWidthPx = widthPx;
    let newHeightPx = heightPx;
    if (is1to1 && isResponsiveGroupResize) {
      const allowedHeightForShape = OCTAGON_CONSTANT * (originalWidth - 2 * borderWidth);

      if (heightPx < originalHeight || (contentHeightPx && contentHeightPx <= allowedHeightForShape)) {
        return {
          widthPx: originalWidth,
          heightPx: originalHeight,
        };
      }
      newWidthPx = heightPx;
      newHeightPx = heightPx;
    }

    // set svg dimensions
    svg.setAttribute('width', `${newWidthPx}px`);
    svg.setAttribute('height', `${newHeightPx}px`);

    // set fill dimensions
    svgFill.setAttribute('d', generateOctagonFillPath({ widthPx: newWidthPx, heightPx: newHeightPx, borderWidth }));
    // set border dimensions
    svgBorder.setAttribute('d', generateOctagonBorderPath({ widthPx: newWidthPx, heightPx: newHeightPx, borderWidth }));
    return { widthPx: newWidthPx, heightPx: newHeightPx };
  }
};

const Octagon = ({ widthPx, heightPx, border, id, fillColor, fillPercent, ...other }: ShapeProps): ReactElement => {
  const { width: borderWidth } = border;
  const iconProps = {
    widthPx,
    heightPx,
    border,
    id,
    fillColor,
    fillPercent,
    fillPath: generateOctagonFillPath({ widthPx, heightPx, borderWidth }),
    borderPath: generateOctagonBorderPath({ widthPx, heightPx, borderWidth }),
    ...other,
  };

  return generateSVGIcon(iconProps);
};

export default Octagon;
